import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Mail } from 'lucide-react'
import { claimAccount, claimAccountOpen } from '../api/auth'
import { fetchContent, has, text } from '../api/content'
import BuildCredit from './BuildCredit'

/**
 * Where an existing family sets up their login for the first time (D-155).
 *
 * **The screen is written around one constraint:** the server answers
 * identically whether or not the address is on file, so this must never say
 * "we found you" or "we didn't". Anything warmer would turn the form into a
 * membership oracle — type an address, learn whether that family dances here —
 * and the studio's roster is not public.
 *
 * That makes the confirmation deliberately non-committal, and the copy has to
 * work for a parent who typed their address correctly *and* for one who typed
 * the wrong one. Hence the plain instruction about what to do if nothing
 * arrives, sitting right next to the success message rather than hidden behind
 * a support page.
 */
export default function ClaimAccountView() {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  /*
   * The door may be shut (D-614). Hiding the link on the sign-in page does not
   * remove this URL, and a family who bookmarked it deserves a sentence rather
   * than a form that is refused when she presses the button.
   *
   * Three answers, not two: open, shut, and <i>could not ask</i>. The last one
   * is a failed lookup and must never render as a statement about the studio
   * (D-209) - a deploy refuses every database-backed request for about 75
   * seconds while this page loads perfectly, so "not open just yet" would be a
   * confident lie told to whoever arrives during it.
   */
  const { data: open, isError } = useQuery({
    queryKey: ['claim-account-available'],
    queryFn: claimAccountOpen,
    staleTime: Infinity,
  })

  // The studio's own number, so the recovery path names a channel that
  // exists. This screen is routed outside the public shell, which is the only
  // place that number otherwise appears.
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const queryClient = useQueryClient()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!email.trim()) return

    setBusy(true)
    setProblem(null)

    try {
      const answer = await claimAccount(email.trim())

      /*
       * <b>The server's word wins, and it is the only one on the wire</b>
       * (D-614). The door can shut between this page loading and the button
       * being pressed - the answer above is cached for the session - and
       * reporting success anyway would tell a family a link was on its way and
       * good for thirty days when none was issued and none ever will be.
       *
       * Written back to the query rather than held beside it, so the whole
       * screen agrees rather than showing a form and a refusal at once.
       */
      if (answer.open === false) {
        queryClient.setQueryData(['claim-account-available'], false)
        return
      }

      setSent(true)
    } catch {
      // The only failure reachable here is the request itself — the server
      // returns OK whatever it found.
      setProblem('We could not reach the studio just now. Please try again in a moment.')
    } finally {
      setBusy(false)
    }
  }

  return (
    /*
     * The one no-session screen that is not an `AuthLayout` (D-225).
     *
     * It has its own header and its own success card, so moving it onto the
     * shared shell would be a redesign of a screen a family sees once — out of
     * scope here. The credit is added in place instead, and the mismatch is
     * left visible rather than papered over: this is the thirteenth width the
     * width sweep was about.
     */
    <div className="flex min-h-full flex-col">
      <div className="mx-auto w-full max-w-[26rem] flex-1 px-5 py-14">
        <header className="mb-6">
          <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">
            Cabot Dance Academy
          </p>
          <h1 className="mt-1 text-[24px] font-bold">Set up your account</h1>
          <p className="mt-2 text-[13.5px] leading-relaxed text-ink-3">
            Already a studio family? Enter the email address the studio has for you and we
            will send a link to set your password.
          </p>
        </header>

        {isError ? (
          /*
           * A failed lookup, said as one (D-209). Not "closed", which would be
           * a claim about the studio; not the form, which might be refused.
           */
          <div className="rounded-2xl border border-line-2 bg-surface-2 px-5 py-5">
            <p className="text-[14px] font-semibold">We could not reach the studio</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
              Please try again in a moment. If the studio has just updated the app it can take
              a minute to come back.
            </p>
          </div>
        ) : open === undefined ? (
          // Nothing until the answer arrives. Showing the form first and taking
          // it away is how somebody submits into a door that is shut.
          <p className="text-[13px] text-ink-3">One moment&hellip;</p>
        ) : open === false ? (
          <div className="rounded-2xl border border-line-2 bg-surface-2 px-5 py-5">
            <p className="text-[14px] font-semibold">Not open just yet</p>
            <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
              The studio is sending out logins a few families at a time. Yours will arrive by
              email when it is your turn, and the link in it sets everything up.
            </p>
            <p className="mt-3 text-[12.5px] leading-relaxed text-ink-2">
              Already had one and cannot get in?{' '}
              <Link to="/forgot-password" className="font-semibold text-accent">
                Reset your password
              </Link>
              .
            </p>
          </div>
        ) : sent ? (
          <div className="rounded-2xl border border-good bg-good-bg px-5 py-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} strokeWidth={2} className="mt-0.5 shrink-0 text-good" />
              <div>
                <p className="text-[14px] font-semibold">Check your email</p>
                {/*
                  Deliberately says "if". The server does not tell us whether the
                  address matched, and this screen must not imply that it did.
                */}
                <p className="mt-1 text-[13px] leading-relaxed text-ink-2">
                  If <strong className="wrap-anywhere">{email.trim()}</strong> is the address
                  on file, a link is on its way. It is good for 30 days.
                </p>
              </div>
            </div>

            {/*
              No longer sends anybody to Facebook (D-625).

              The studio's address for a family is editable in the app now
              (D-613), and a recovery path that leads to another company's inbox
              is one the studio cannot improve.

              **It names the number rather than saying "ring the studio".** This
              screen is routed outside the public shell, which is the only place
              that number otherwise appears - so an earlier draft told a parent
              to ring somebody on a page carrying no way to do it. The number is
              the `studio.phone` block, so Tonya owns it (A-16), and the sentence
              falls back to what it can honestly say when the block is missing.
            */}
            <p className="mt-4 border-t border-good/30 pt-3.5 text-[12.5px] leading-relaxed text-ink-2">
              Nothing arrived? Check the junk folder first. If it is still not there, the
              studio may have a different address for you
              {has(content, 'studio.phone') ? (
                <>
                  {' '}&mdash; ring the studio on{' '}
                  <a
                    href={`tel:${text(content, 'studio.phone').replace(/[^\d+]/g, '')}`}
                    className="font-semibold text-accent"
                  >
                    {text(content, 'studio.phone')}
                  </a>{' '}
                  and they can put it right.
                </>
              ) : (
                <> &mdash; ring the studio and they can put it right.</>
              )}
            </p>

            <button
              type="button"
              onClick={() => {
                setSent(false)
                setEmail('')
              }}
              className="mt-3 text-[12.5px] font-semibold text-accent underline underline-offset-2"
            >
              Try a different address
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-[12px] font-semibold text-ink-3">Email address</span>
              <div className="flex items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2.5 focus-within:border-accent">
                <Mail size={15} strokeWidth={2} className="shrink-0 text-ink-3" />
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-ink-3"
                />
              </div>
            </label>

            {problem && (
              <p role="alert" className="text-[13px] text-crit">
                {problem}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="rounded-lg bg-accent px-4 py-2.5 text-[14px] font-semibold text-on-accent disabled:opacity-40"
            >
              {busy ? 'Sending...' : 'Send me a link'}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-[13px] text-ink-3">
          Already set up? <Link to="/signin" className="font-semibold text-accent">Sign in</Link>
        </p>
      </div>

      <BuildCredit tone="quiet" />
    </div>
  )
}
